/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Inject, Injectable, OnDestroy } from "@angular/core";
import { BusEvent, clientUtil, EventBusService, Logger } from "@html-core";
import { ConnectService } from "../../../shared/common/service/connect.service";
import { JscdkCommonInvoker } from "../../../shared/common/jscdk/jscdk-common-invoker";
import { globalArray } from "../../../shared/jscdk/jscdkClient";
import { ShareFolderModel } from "../../../shared/common/model/share-folder-model";
import { ConnectionServerModel } from "../../../shared/common/model/connection-server-model";
import { ModalDialogService } from "../../../shared/common/commondialog/dialog.service";
import { ViewService } from "../../../shared/launcher/common/view-service";
import { RootModel } from "../../../shared/common/model/root-model";
import { IdleSessionService } from "../../../shared/common/service/idle-session.service";
import { WindowToken } from "../../../shared/common/window.module";
import { BaseViewService } from "../../../shared/common/service/base-view.service";
import { ActiveClientService } from "../../../iwa/app-root/active-client.service";

@Injectable()
export class FileAssociationService implements OnDestroy {
   public isStartFromFileAssociation: boolean = false;
   public fileList = [];
   public faMsg = [];
   public isFromFA: boolean = true;
   public static readonly mockedFolderForFA = "(VMFR)HorizonChromeFA";
   private newHost;
   private mockedEntry;
   private currentHost: string;
   public serverAddress: string;
   public faUrl: string;
   private iwaFileInfo = null;

   ngOnDestroy() {
      this.mockedEntry = null;
      this.shareFolderModel.removeFAEntry();
   }

   constructor(
      private connectService: ConnectService,
      private jscdkCommonInvoker: JscdkCommonInvoker,
      private shareFolderModel: ShareFolderModel,
      private connectionServerModel: ConnectionServerModel,
      private eventBusService: EventBusService,
      private modalDialogService: ModalDialogService,
      private baseViewService: BaseViewService,
      private viewService: ViewService,
      private rootModel: RootModel,
      private idleSessionService: IdleSessionService,
      @Inject(WindowToken) private window: Window,
      private activeClientService: ActiveClientService
   ) {
      this.initFAFolder();
      if (clientUtil.isChromeClient()) {
         chrome.runtime.onMessage.addListener(this.onMsgHandler);
      } else if (clientUtil.isIwaClient()) {
         this.window.addEventListener("message", (event) => {
            this.onIwaMsgHandler(event);
         });
      }

      this.eventBusService.listen("logOutSuccess").subscribe(() => {
         this.resetMockedFolder();
      });
   }

   private launchFASession = (request) => {
      this._updateHostInfo(request.brokerId);
      this.isFromFA = true;
      this._connectToBroker(request.brokerId, request.url);
      this.eventBusService.dispatch(new BusEvent.FileAssociationLoadingMsg(true, request.brokerId));
      if (!this.rootModel.get("isAlreadyLogin") || this.idleSessionService.isSessionTimedOut()) {
         Logger.info("Bring the main window to front.");
         let mainWindow;
         if (clientUtil.isChromeClient()) {
            mainWindow = chrome.app.window.current();
         } else {
            mainWindow = this.window;
         }
         mainWindow.restore();
         mainWindow.focus();
      }
   };

   private onMsgHandler = (request, sender, sendResponse?) => {
      if (sender.url.indexOf("file-association.html") >= 0) {
         this._redirectFile();
         if (this.getChromeclientContent()) {
            this.launchFASession(request);
            clientUtil.isFAwithMainClientHide = this.window.chromeClient.isFAwithMainClientHide;
         }
      }
   };

   private onIwaMsgHandler = async (event) => {
      const fileMsg = event.data;
      if (fileMsg.type && fileMsg.type === "openFAApplication") {
         this.iwaFileInfo = fileMsg.IwaFileInfo;
         // restore main window since it was minimized after launching FA menu
         this.window.restore();
         this.window.focus();
         this._redirectFile();
         this.launchFASession(fileMsg);
      } else if (fileMsg.type && fileMsg.type === "closeFAMenu" && !this.activeClientService.getActiveClient()) {
         Logger.info("Close on FA menu window close since no active client.");
         this.window.close();
      }
   };

   private getChromeclientContent = () => {
      //make this as a separate function for UT
      return this.window.chromeClient;
   };

   private resetMockedFolder = () => {
      this.mockedEntry = null;
      this.shareFolderModel.removeFAEntry();
      this.initFAFolder();
   };

   private _showLogoutDialog = () => {
      //Logout dialog will show when hover on header buttons,
      //This is a workaround, will remove when I find the root cause
      //@ts-ignore
      const logOutBtn = this.window.document.getElementById("logoutBtn");
      const hoverEvent = new MouseEvent("mouseover", {
         //@ts-ignore
         view: this.window,
         bubbles: true,
         cancelable: true
      });
      logOutBtn.dispatchEvent(hoverEvent);
   };

   private _updateHostInfo = (brokerURL) => {
      if (clientUtil.isChromeClient()) {
         this.currentHost = this.connectionServerModel.host;
      } else if (clientUtil.isIwaClient() && this.connectionServerModel.host !== this.window.location.host) {
         // Only set the curent host if connectionServerModel.host is a valid connection server host
         // Otherwise currentHost will be the app ID and fail the logic for the later check in connectToBroker
         this.currentHost = this.connectionServerModel.host;
      }
      this.newHost = brokerURL;
      this.connectionServerModel.updateComingHost(brokerURL);
   };

   private _connectToBroker = (hostAddress, url) => {
      this.serverAddress = hostAddress;
      if (hostAddress && url) {
         Logger.info("Connect to broker, launch app for FA in server: " + hostAddress);
         if (this.currentHost && this.currentHost !== "") {
            if (this.currentHost !== this.newHost) {
               Logger.info("fa launch on the different broker session.");
               this.rootModel.set("faServer", hostAddress);
               Logger.info("Clear entry when broker is different");
               this.mockedEntry = null;
               this.modalDialogService.showConfirm({
                  data: {
                     titleKey: "dialog_title_logout",
                     contentKey: "dialog_confirm_logout"
                  },
                  callbacks: {
                     confirm: () => {
                        this.jscdkCommonInvoker.logout(false);
                        this.baseViewService.isForFA = true;
                        this.viewService.isForFA = true;
                        this.faUrl = url;
                     }
                  }
               });
               this._showLogoutDialog();
               return;
            } else {
               Logger.debug("fa launch on the same broker session.");
               this.connectService.setURI(hostAddress, () => {
                  this.jscdkCommonInvoker.setURI(url, hostAddress);
               });
            }
         } else {
            Logger.info("fa launch with no active broker session.");
            this.connectService.setURI(hostAddress, () => {
               this.jscdkCommonInvoker.setURI(url, hostAddress);
            });
         }
      }
   };

   private _redirectFile = () => {
      //@ts-ignore
      const fileInfo = this.getLatestFileInfo();
      if (fileInfo) {
         let fileName;
         if (clientUtil.isIwaClient()) {
            fileName = "/" + fileInfo.entry.name;
         } else {
            fileName = fileInfo.entry.fullPath;
         }
         globalArray["file-association"] = [];
         if (fileName) {
            globalArray["file-association"].push(
               FileAssociationService.mockedFolderForFA + fileName.replace("/", "\\")
            );
         }

         if (clientUtil.isIwaClient()) {
            this.eventBusService.dispatch({
               type: BusEvent.FileAssociationNewFile.MSG_TYPE,
               data: fileInfo
            });
         } else {
            this.updateFileInfo(fileInfo);
         }
      }
   };

   public updateFileInfo = (fileInfo) => {
      const mockedFolderPath = "~/" + FileAssociationService.mockedFolderForFA;
      if (fileInfo) {
         const localFileInfo = {
            entry: fileInfo.entry,
            addedInFolder: false
         };
         localFileInfo.entry.isFA = true;

         this.fileList.push(localFileInfo);
         for (let i = 0; i < this.fileList.length; i++) {
            if (!this.fileList[i].addedInFolder) {
               Logger.info("Update mocked folder with local file information: " + this.fileList[i].entry.name);
               this.mockedEntry.addFileToMockedFolder(this.fileList[i].entry);
               this.fileList[i].addedInFolder = true;
               if (clientUtil.isChromeClient()) {
                  this.eventBusService.dispatch({
                     type: BusEvent.FileAssociationNewFile.MSG_TYPE
                  });
               }
               break;
            }
         }
      }
      this.shareFolderModel.addFAFolder(mockedFolderPath, this.mockedEntry);
   };

   public initFAFolder = () => {
      if (!this.mockedEntry) {
         Logger.info("Create mocked folder for file association.");
         this.mockedEntry = new SpyFolderEntry();
      }
      const mockedFolderPath = "~/" + FileAssociationService.mockedFolderForFA;
      this.shareFolderModel.addFAFolder(mockedFolderPath, this.mockedEntry);
   };

   public getLatestFileInfo = () => {
      if (clientUtil.isChromeClient()) {
         const windows = chrome.app.window.getAll();
         for (let i = 0; i < windows.length; i++) {
            if (windows[i].id === "HTMLAccessChromeWindow") {
               //@ts-ignore
               return windows[i].contentWindow.fileInfo;
            }
         }
         return null;
      } else if (clientUtil.isIwaClient()) {
         return this.iwaFileInfo;
      }
      return null;
   };
}

// Wrapper for IWA FileSystemFileHandle object, similar to
// HTML5FileSystemFileHandler
class FAFileEntry {
   private name;
   private isDirectory: boolean = false;
   private isFile: boolean = true;
   public isFA: boolean = true;
   private fullPath: string;
   private metaData;
   private fileHandler;
   private fileEntry;
   private parentPath: string;
   public writer = {
      handler: null,
      addedInCache: false
   };

   constructor() {}

   private setMetadata = (fileEntry) => {
      this.metaData = {
         path: "/" + FileAssociationService.mockedFolderForFA + "/" + fileEntry.name,
         name: fileEntry.name,
         size: fileEntry.size,
         type: fileEntry.type,
         webkitRelativePath: fileEntry.webkitRelativePath,
         lastModified: fileEntry.lastModified,
         modificationTime: fileEntry.lastModifiedDate
      };
   };

   public async initialize(handler) {
      this.fileHandler = handler;
      this.fileEntry = await handler.getFile();
      this.name = this.fileEntry.name;
      this.fullPath = "/" + this.name;
      this.parentPath = "/" + FileAssociationService.mockedFolderForFA;
      this.setMetadata(this.fileEntry);
   }

   public file = (FuncOnLoad, FuncOnError?) => {
      return new Promise((resolve, reject) => {
         if (FuncOnLoad) {
            FuncOnLoad(this.fileEntry);
            resolve("");
         }
      });
   };

   public createWriter = async (FuncOnLoad, FuncOnError?) => {
      const permission = await this.fileHandler.requestPermission({ mode: "readwrite" });
      if (permission === "granted") {
         if (!this.writer.handler) {
            this.writer.handler = await this.fileHandler.createWritable({ keepExistingData: true });
         }
         return new Promise(async (resolve, reject) => {
            try {
               if (FuncOnLoad) {
                  FuncOnLoad(this.writer.handler);
                  this.fileEntry = await this.fileHandler.getFile();
                  if (this.fileEntry.size !== this.metaData.size) {
                     this.setMetadata(this.fileEntry);
                  }
                  resolve("");
               }
            } catch (error) {
               if (FuncOnError) {
                  FuncOnError(error);
               }
               reject(error);
            }
         });
      } else {
         Logger.info("CreateWriter: user denied permission.");
         return Promise.reject(new Error("CreateWriter: Permission denied"));
      }
   };

   public getMetadata = async (onLoad, FuncOnError) => {
      try {
         onLoad(this.metaData);
      } catch (e) {
         FuncOnError(e);
      }
   };
}

class SpyFolderEntry {
   private filesystem;
   private fullPath: string = "/" + FileAssociationService.mockedFolderForFA;
   private isDirectory: boolean = true;
   private isFile: boolean = false;
   private isFA: boolean = true;
   private name: string = FileAssociationService.mockedFolderForFA;
   private fileEntry = [];
   public createWriter;
   public createReader;
   public file;
   public getMetadata;

   constructor() {
      this.createWriter = this.createWriterFunc;
      this.file = this.fileFunc;
      this.getMetadata = this.getMetaDataFunc;
      this.createReader = this.createReaderFunc;
   }

   // Return index of file in file entry array or -1 if not found
   private getFileIndex = async (fileHandle) => {
      try {
         for (let i = 0; i < this.fileEntry.length; i++) {
            const isSame = await this.fileEntry[i].fileHandler.isSameEntry(fileHandle);
            if (isSame) {
               return i;
            }
         }
         return -1;
      } catch (e) {
         return -1;
      }
   };

   public addFileToMockedFolder = async (fileEntryInfo) => {
      if (!fileEntryInfo) {
         Logger.warning("File entry is empty.");
         return;
      }
      if (fileEntryInfo.filesystem) {
         this.filesystem = fileEntryInfo.filesystem;
      }
      if (this.fileEntry.indexOf(fileEntryInfo) === -1) {
         if (clientUtil.isIwaClient()) {
            const fileIndex = await this.getFileIndex(fileEntryInfo);
            if (fileIndex === -1) {
               let faFileEntry = new FAFileEntry();
               await faFileEntry.initialize(fileEntryInfo);
               this.fileEntry.push(faFileEntry);
               Logger.debug("<cdr> Added new file to folder: " + fileEntryInfo.name);
            } else {
               const updatedFileEntry = await fileEntryInfo.getFile();
               this.fileEntry[fileIndex].fileEntry = updatedFileEntry;
               Object.assign(this.fileEntry[fileIndex].metaData, {
                  size: updatedFileEntry.size,
                  type: updatedFileEntry.type,
                  webkitRelativePath: updatedFileEntry.webkitRelativePath,
                  lastModified: updatedFileEntry.lastModified,
                  modificationTime: updatedFileEntry.lastModifiedDate
               });
               Logger.debug("<cdr> Updated content for " + this.fileEntry[fileIndex].name);
            }
         } else {
            this.fileEntry.push(fileEntryInfo);
            Logger.debug("<cdr> new file is added");
         }
      }
   };

   private createWriterFunc = (FuncOnWrite, FuncOnError?): void => {
      try {
         Logger.debug("<cdr> spy FolderEntry for FA CreateWriter");
         FuncOnWrite();
      } catch (e) {
         if (FuncOnError) {
            FuncOnError();
         }
      }
   };

   private fileFunc = (FuncOnLoad, FuncOnError?): void => {
      try {
         Logger.debug("<cdr> spy FileFunc for FA FileFunc");
         FuncOnLoad();
      } catch (e) {
         if (FuncOnError) {
            FuncOnError();
         }
      }
   };

   private getMetaDataFunc = (onLoad, FuncOnError) => {
      const mockedMetadata = {
         size: 0,
         modificationTime: new Date()
      };
      try {
         onLoad(mockedMetadata);
      } catch (e) {
         FuncOnError();
      }
   };

   private createReaderFunc = () => {
      Logger.debug("<cdr> spy CreateReader for FA FileFunc");
      const mockedRes = [];
      for (let i = 0; i < this.fileEntry.length; i++) {
         mockedRes.push(this.fileEntry[i]);
      }
      let count = 0;
      return {
         readEntries: (onLoad, onError) => {
            try {
               if (count > 0 && mockedRes.length > 0) {
                  mockedRes.shift();
               }
               count++;
               onLoad(mockedRes);
            } catch (e) {
               onError();
            }
         }
      };
   };
}
